import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mapeo de rutas a nombres legibles
const routeLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  conversaciones: 'Conversaciones',
  tickets: 'Tickets',
  actividades: 'Actividades',
  proyectos: 'Proyectos',
  conocimiento: 'Base de Conocimiento',
  clientes: 'Clientes',
  reportes: 'Reportes',
  configuracion: 'Configuración',
  notificaciones: 'Notificaciones',
  perfil: 'Mi Perfil',
  buscar: 'Búsqueda',
  nuevo: 'Nuevo',
  editar: 'Editar',
  detalle: 'Detalle',
  kanban: 'Kanban',
};

interface BreadcrumbItem {
  label: string;
  path: string;
  isLast: boolean;
}

interface BreadcrumbsProps {
  className?: string;
  customLabels?: Record<string, string>;
  homeLabel?: string;
}

export function Breadcrumbs({
  className,
  customLabels = {},
  homeLabel = 'Inicio'
}: BreadcrumbsProps) {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Si estamos en la raíz o dashboard, no mostrar breadcrumbs
  if (pathSegments.length === 0 || (pathSegments.length === 1 && pathSegments[0] === 'dashboard')) {
    return null;
  }

  const breadcrumbs: BreadcrumbItem[] = pathSegments.map((segment, index) => {
    const path = '/' + pathSegments.slice(0, index + 1).join('/');
    const isLast = index === pathSegments.length - 1;

    // Intentar obtener label personalizado, luego del mapeo, luego formatear el segmento
    let label = customLabels[segment] || routeLabels[segment.toLowerCase()];

    if (!label) {
      // Si parece un UUID, mostrar algo genérico
      if (segment.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        label = 'Detalle';
      } else {
        // Capitalizar y reemplazar guiones/underscores
        label = segment
          .replace(/[-_]/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase());
      }
    }

    return { label, path, isLast };
  });

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn('flex items-center space-x-1 text-sm', className)}
    >
      <Link
        to="/dashboard"
        className="flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <Home className="w-4 h-4" />
        <span className="sr-only">{homeLabel}</span>
      </Link>

      {breadcrumbs.map((item) => (
        <div key={item.path} className="flex items-center">
          <ChevronRight className="w-4 h-4 text-gray-400 mx-1" />
          {item.isLast ? (
            <span
              className="text-gray-900 dark:text-white font-medium"
              aria-current="page"
            >
              {item.label}
            </span>
          ) : (
            <Link
              to={item.path}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {item.label}
            </Link>
          )}
        </div>
      ))}
    </nav>
  );
}

// Hook para usar breadcrumbs personalizados desde las páginas
interface UseBreadcrumbsOptions {
  items?: Array<{ label: string; path?: string }>;
}

export function useBreadcrumbItems({ items }: UseBreadcrumbsOptions = {}) {
  // Este hook puede ser expandido para integrar con un context
  // que permita a las páginas hijas establecer breadcrumbs dinámicos
  return items || [];
}
